import { format, parseISO } from 'date-fns';

export const formatPhoneToApi = (phone: string | null) => {
  if (!phone) {
    return '';
  }
  return phone.indexOf('+55') === 0
    ? phone.replace(/[^0-9+]/gi, '')
    : `+55${phone.replace(/[^0-9+]/gi, '')}`;
};

export const formatPhoneLead = (phone: string | null) => {
  if (!phone) {
    return '';
  }
  return formatPhone(phone).replace('+55', '');
};

export const formatPhone = (phoneNumber: string | number) => {
  if (typeof phoneNumber === 'string') {
    phoneNumber = parseInt(phoneNumber.replace(/[^0-9]/gi, ''), 10);
  }
  if (typeof phoneNumber === 'number') {
    const phone = phoneNumber.toString();
    if (phone.length === 13) {
      return `+${phone.slice(0, 2)} (${phone.slice(2, 4)}) ${phone.slice(
        4,
        9
      )}-${phone.slice(9, 13)}`;
    } else if (phone.length === 12) {
      return `+${phone.slice(0, 2)} (${phone.slice(2, 4)}) ${phone.slice(
        4,
        8
      )}-${phone.slice(8, 12)}`;
    } else if (phone.length === 11) {
      return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7, 11)}`;
    } else if (phone.length === 10) {
      return `(${phone.slice(0, 2)}) ${phone.slice(2, 6)}-${phone.slice(6, 10)}`;
    } else if (phone.length === 9) {
      return `${phone.slice(0, 5)}-${phone.slice(5, 9)}`;
    } else if (phone.length === 8) {
      return `${phone.slice(0, 4)}-${phone.slice(4, 8)}`;
    }
  }
  return '';
};

export const formatCNPJ = (value: number | string): string => {
  if (typeof value === 'number') {
    value = value.toString();
  }

  if (value.length === 14) {
    return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(
      5,
      8
    )}/${value.slice(8, 12)}-${value.slice(12, 14)}`;
  }

  return value;
};

export const formatCPF = (value: number | string): string => {
  if (typeof value === 'number') {
    value = value.toString();
  }

  if (value.length === 11) {
    return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(
      6,
      9
    )}-${value.slice(9, 11)}`;
  }

  return value;
};

export const formatDocument = (value: number | string): string => {
  if (!value && value !== 0) {
    return '';
  }

  if (typeof value === 'number') {
    value = value.toString();
  }

  if (value.length === 11) {
    return formatCPF(value);
  }

  return formatCNPJ(value);
};

export const formatArea = (
  value: number | string,
  maxDecimal = 0,
  minDecimal = 0
): string | null => {
  const formattedValue = formatNumber(value, maxDecimal, minDecimal);
  return formattedValue != null ? `${formattedValue} m²` : null;
};

export const formatCurrency = (
  value: number | string,
  maxDecimal = 2,
  minDecimal = 2
): string => {
  const formattedValue = formatNumber(value, maxDecimal, minDecimal);
  return formattedValue != null ? `R$ ${formattedValue}` : 'R$ 0,00';
};

export const formatNumber = (
  value: number | string,
  maxDecimal = 0,
  minDecimal = 0
): string | null => {
  if (!value && value !== 0) {
    return null;
  }

  if (typeof value !== 'number') {
    value = parseFloat(value) || 0;
  }

  return value.toLocaleString('pt-BR', {
    maximumFractionDigits: maxDecimal,
    minimumFractionDigits: minDecimal,
  });
};

export const formatCategory = (value: string | null, t: any) => {
  if (!value) {
    return;
  }

  return t(`realestate:categories.${value.toLowerCase()}`);
};

export const formatBoolean = (value: string | null) => {
  return value === 'true' ? 'Sim' : 'Não';
};

export const formatDate = (value: string): string => {
  if (!value) {
    return '';
  }

  return format(parseISO(value), 'dd/MM/yyyy');
};

export const formatDateTime = (value: string, showSeconds = false): string => {
  if (!value) {
    return '';
  }
  if (!showSeconds) {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } else {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm:ss');
  }
};

export const formatTime = (value: string): string => {
  if (!value) {
    return '';
  }

  return format(parseISO(value), 'HH:mm');
};

export const formatLocation = (location: any): string => {
  if (!location) {
    return '';
  }

  const cityString = [
    location.city && location.city.name,
    location.city && location.city.state && location.city.state.name,
  ]
    .filter((x) => x)
    .join(' - ');

  return [location.street, location.streetNumber, location.district, cityString]
    .filter((x) => x)
    .join(', ');
};

export const formatRealtyThreadSubject = (value: string): string => {
  switch (value) {
    case 'REALTY_PROBLEM':
      return `Imóvel Irregular`;
    case 'PARTNERSHIP':
      return `Parceria`;
    default:
      return '';
  }
};

export const formatPercent = (value: number | string, decimal?: number) => {
  if (typeof value === 'string') {
    value = parseFloat(value) || 0;
  }
  value = value * 100;

  if (decimal != null) {
    return `${value.toFixed(decimal)}%`;
  }
  decimal = 0;
  let strValue = value.toFixed(decimal);
  while (parseFloat(strValue) <= 0 && decimal <= 10) {
    decimal++;
    strValue = value.toFixed(decimal);
  }
  return `${strValue}%`;
};

export const formatZipcode = (zipcode: string | null) => {
  zipcode = (zipcode || '').replace(/[^0-9]/g, '').slice(0, 8);

  if (zipcode.length === 0) {
    return '';
  }

  return zipcode.slice(0, 5) + '-' + zipcode.slice(5, 8);
};

export const clearBRLNumber = (text: string | number): number => {
  try {
    if (typeof text === 'number') return text;
    if (text.length < 1) return parseFloat('0');
    if (text.indexOf('R$')) return parseFloat(text);
    text = text.replace('R$', '').trim();
    text = text.replace(/\./g, '');
    text = text.replace(',', '.');
    return parseFloat(text);
  } catch (e) {
    return parseFloat('0');
  }
};
